'use client';

import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useFilterSetUtils } from 'src/hooks/filters/useFilterSetUtils';
import useTableFilterSelections from 'src/hooks/filters/useTableFilterSelections';
import { useEditDrawerData } from 'src/hooks/common/useEditDrawerData';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faSliders,
  faCaretDown,
  faClose,
  faPencil,
} from '@fortawesome/pro-solid-svg-icons';
import { EmptyState } from 'src/components/PulseUI';
import { ParentFilterGroup } from 'src/context/reducers/filterSetReducer';
import { FilterType } from 'src/context/reducers/filterSetTypes';
import {
  SITE_OF_CARE_DISPLAY_TYPE,
  collectSiteOfCareLabels,
  countAppliedFilterTypes,
  isSocFilterType,
  isSocSuppressedFilterType,
} from 'src/components/FilterSets/SiteOfCare/displayHelpers';
import { useSocOptions } from 'src/components/FilterSets/SiteOfCare';
import type { SiteOfCareType } from 'src/api/redux/suggestions/suggestionsApi';

const getDisplayTypeForFilterType = (type: string): string => {
  if (isSocFilterType(type)) {
    return SITE_OF_CARE_DISPLAY_TYPE;
  }
  if (type === FilterType.SITE_SPECIALTY) {
    return 'Site Specialty';
  }
  let displayType =
    type.charAt(0).toUpperCase() +
    type.slice(1).toLowerCase().replace(/_/g, ' ');
  if (displayType === 'Payer type') {
    displayType = 'Payer';
  }
  return displayType;
};

interface TableFiltersButtonProps {
  location: string;
}

const TableFiltersButton = ({ location }: TableFiltersButtonProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { closeAllButCurrent } = useDrawer();

  const { groupedFilterSets } = useFilterSetUtils({
    filterType: 'FILTER',
  });

  const filterSets = useMemo(() => {
    return groupedFilterSets['FILTER'] || [];
  }, [groupedFilterSets]);

  const {
    tempSelectedFilterPreset,
    selectedFilterPreset,
    clearFilterPreset,
    setSelectedFilterPreset,
    setTempSelectedFilterPreset,
  } = useTableFilterSelections(location);

  const { setCurrentFilterSetData } = useEditDrawerData();
  const { socOptions } = useSocOptions();

  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'personal' | 'company'>(
    'personal'
  );

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Use the appropriate preset based on the type, falling back to selectedFilterPreset if tempSelectedFilterPreset is not present
  const currentTempPreset = useMemo(() => {
    return tempSelectedFilterPreset || selectedFilterPreset;
  }, [tempSelectedFilterPreset, selectedFilterPreset]);

  // Number of applied filters. SOC_TYPE and SOC_SUBTYPE are two backend rows
  // driven by a single Site of Care picker, so countAppliedFilterTypes collapses
  // them; counting raw rows showed one "Hospital > Childrens" selection as 2.
  const filterCount = useMemo(() => {
    const preset = tempSelectedFilterPreset || selectedFilterPreset;
    if (!Array.isArray(preset?.filters)) return 0;
    return countAppliedFilterTypes(preset.filters);
  }, [tempSelectedFilterPreset, selectedFilterPreset]);

  const handlePresets = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  // Flatten the filter sets into an array
  const filterSetsList: ParentFilterGroup[] = useMemo(() => {
    return filterSets.map((fs) => fs as unknown as ParentFilterGroup);
  }, [filterSets]);

  const { personalSets, companySets } = useMemo(() => {
    if (!filterSetsList.length) return { personalSets: [], companySets: [] };

    return {
      personalSets: filterSetsList.filter(
        (filter) => filter.scope !== 'COMPANY'
      ),
      companySets: filterSetsList.filter(
        (filter) => filter.scope === 'COMPANY'
      ),
    };
  }, [filterSetsList]);

  const handleFilterSetClick = (filterSet: ParentFilterGroup) => {
    setSelectedFilterPreset(filterSet);
    setTempSelectedFilterPreset(filterSet);

    // Close the dropdown
    setIsOpen(false);
  };

  const handleEditItem = useCallback(
    (e: React.MouseEvent, item) => {
      e.stopPropagation();
      setCurrentFilterSetData(item);
      closeAllButCurrent(`editFilterDrawer-${location}`);
    },
    [closeAllButCurrent, location]
  );

  // Display text for the button - similar to PresetsDropdown
  const displayText = useMemo(() => {
    if (!currentTempPreset) return 'Filters';
    return currentTempPreset?.label;
  }, [currentTempPreset]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className="h-9 px-3.5 py-2 bg-white rounded-full outline outline-1 -outline-offset-1 outline-neutral-300 inline-flex justify-center items-center gap-2 hover:bg-neutral-050"
        onClick={handlePresets}
        aria-label="Open filters"
        data-testid={`encounters-toolbar-${location}`}
      >
        <div className="w-3.5 h-4 overflow-hidden flex items-center justify-center">
          <Icon icon={faSliders} className="w-3 h-3 text-primary" />
        </div>
        <span className="text-primary text-sm font-semibold font-['Inter'] leading-tight truncate max-w-32">
          {displayText}
        </span>
        <div className="px-2 py-1 bg-sky-50 rounded-full flex justify-center items-center gap-2 overflow-hidden">
          <div className="text-center justify-start text-sky-600 text-xs font-semibold font-['Inter'] leading-none">
            {filterCount}
          </div>
        </div>
        {currentTempPreset && (
          <div
            className="w-4 h-4 flex items-center justify-center cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              clearFilterPreset?.();
            }}
          >
            <Icon
              icon={faClose}
              className="w-3 h-3 text-neutral-600 transition-all duration-300"
            />
          </div>
        )}
        <div className="w-4 h-4 flex items-center justify-center">
          <Icon
            icon={faCaretDown}
            className={`w-3 h-3 text-neutral-600 transition-all duration-300 ${
              isOpen ? '-rotate-90' : 'rotate-0'
            }`}
          />
        </div>
      </button>
      {isOpen && (
        <div className="min-w-96 max-h-96 inset-x-0 top-full mt-1 absolute bg-white rounded-xl shadow-md outline outline-1 outline-offset-[-1px] outline-neutral-300 inline-flex flex-col justify-start items-start z-50">
          <div className="self-stretch px-6 pt-2 pb-0 border-b border-neutral-300 inline-flex justify-start items-end gap-4">
            <div
              className="flex-1 py-1 relative flex justify-center items-center gap-1 overflow-hidden cursor-pointer"
              onClick={() => setActiveTab('personal')}
            >
              <div className="px-3 py-2 rounded-lg flex justify-center items-center">
                <div
                  className={`text-center whitespace-nowrap ${
                    activeTab === 'personal'
                      ? 'text-primary text-sm font-bold'
                      : 'text-secondary text-sm font-semibold'
                  } font-['Inter'] leading-tight`}
                >
                  Personal
                </div>
              </div>
              {activeTab === 'personal' && (
                <div className="w-full h-1 left-0 top-[40px] absolute bg-sky-600 rounded-tl-lg rounded-tr-lg" />
              )}
            </div>
            <div
              className="flex-1 py-1 relative flex justify-center items-center gap-1 overflow-hidden cursor-pointer"
              onClick={() => setActiveTab('company')}
            >
              <div className="px-3 py-2 rounded-lg flex justify-center items-center">
                <div
                  className={`text-center whitespace-nowrap ${
                    activeTab === 'company'
                      ? 'text-primary text-sm font-bold'
                      : 'text-secondary text-sm font-semibold'
                  } font-['Inter'] leading-tight`}
                >
                  Company
                </div>
              </div>
              {activeTab === 'company' && (
                <div className="w-full h-1 left-0 top-[40px] absolute bg-sky-600 rounded-tl-lg rounded-tr-lg" />
              )}
            </div>
          </div>
          {activeTab === 'personal' && (
            <div
              className={`self-stretch p-2 flex flex-col min-h-48 max-h-72 overflow-y-auto ${
                personalSets.length === 0
                  ? 'justify-center items-center'
                  : 'justify-start items-start'
              }`}
            >
              {personalSets.length === 0 ? (
                <EmptyState
                  size="sm"
                  title="No Personal Presets"
                  description="You haven't created any personal presets yet."
                  icon={<Icon icon={faSliders} className="text-neutral-400" />}
                />
              ) : (
                <div className="w-full">
                  {personalSets.map((filter, index) => {
                    // Compare with the currently selected filter set
                    const isSelected =
                      currentTempPreset?.label === filter?.label;
                    return (
                      <div
                        key={`${filter.id}-${index}`}
                        className="group self-stretch p-2 rounded-md flex items-center justify-start gap-2 cursor-pointer hover:bg-neutral-100"
                        onClick={() => handleFilterSetClick(filter)}
                      >
                        <div className="flex-1 inline-flex flex-col justify-start items-start gap-0.5">
                          <div className="inline-flex justify-start items-center gap-2">
                            <div
                              className={`w-4 h-4 relative ${
                                isSelected
                                  ? 'bg-sky-800'
                                  : 'bg-white border border-neutral-400'
                              } rounded-3xl overflow-hidden`}
                            >
                              {isSelected && (
                                <div className="w-2 h-2 left-[4px] top-[4px] absolute bg-white rounded-full" />
                              )}
                            </div>
                            <div className="justify-start text-black text-sm font-semibold font-['Inter'] leading-tight">
                              {filter?.label}
                            </div>
                          </div>
                          <div className="self-stretch pl-6 flex flex-col justify-start items-start gap-1">
                            <div className="self-stretch justify-start">
                              {renderFilterDescription(filter, socOptions)}
                            </div>
                          </div>
                        </div>
                        <div
                          className={`w-0 h-5 opacity-0 flex items-start justify-center group-hover:opacity-100 group-hover:w-5`}
                          onClick={(e) => handleEditItem(e, filter)}
                        >
                          <Icon
                            icon={faPencil}
                            className="w-4 h-4 text-neutral-600"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {activeTab === 'company' && (
            <div
              className={`self-stretch p-2 flex flex-col min-h-48 max-h-72 overflow-y-auto ${
                companySets.length === 0
                  ? 'justify-center items-center'
                  : 'justify-start items-start'
              }`}
            >
              {companySets.length === 0 ? (
                <EmptyState
                  size="sm"
                  title="No Company Presets"
                  description="There are no company-wide presets available."
                  icon={<Icon icon={faSliders} className="text-neutral-400" />}
                />
              ) : (
                <div className="w-full">
                  {companySets.map((filter, index) => {
                    // Compare with the currently selected filter set
                    const isSelected =
                      currentTempPreset?.label === filter?.label;
                    return (
                      <div
                        key={`${filter.id}-${index}`}
                        className="self-stretch p-2 rounded-md inline-flex justify-start items-center gap-2 cursor-pointer hover:bg-neutral-100"
                        onClick={() => handleFilterSetClick(filter)}
                      >
                        <div className="flex-1 inline-flex flex-col justify-start items-start gap-0.5">
                          <div className="inline-flex justify-start items-center gap-2">
                            <div
                              className={`w-4 h-4 relative ${
                                isSelected
                                  ? 'bg-sky-800'
                                  : 'bg-white border border-neutral-400'
                              } rounded-3xl overflow-hidden`}
                            >
                              {isSelected && (
                                <div className="w-2 h-2 left-[4px] top-[4px] absolute bg-white rounded-full" />
                              )}
                            </div>
                            <div className="justify-start text-black text-sm font-semibold font-['Inter'] leading-tight">
                              {filter?.label}
                            </div>
                          </div>
                          <div className="self-stretch pl-6 flex flex-col justify-start items-start gap-1">
                            <div className="self-stretch justify-start">
                              {renderFilterDescription(filter, socOptions)}
                            </div>
                          </div>
                        </div>
                        <div className="w-4 h-4 relative opacity-0">
                          <div className="w-4 h-4 left-0 top-0 absolute overflow-hidden">
                            <div className="w-3 h-3 left-[1.99px] top-[1.99px] absolute bg-neutral-600" />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {/* Clear button at the bottom */}
          <div className="self-stretch p-2 border-t border-neutral-300">
            <button
              type="button"
              className="w-full px-4 py-2 bg-white rounded-full border border-neutral-300 inline-flex justify-center items-center gap-2 hover:bg-neutral-050"
              onClick={() => {
                clearFilterPreset?.();
                setIsOpen(false);
              }}
              disabled={!currentTempPreset}
            >
              <span className="text-primary text-sm font-semibold font-['Inter'] leading-tight">
                Clear Filter
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to render filter description
const renderFilterDescription = (
  filter: ParentFilterGroup,
  socOptions: SiteOfCareType[]
): ReactNode => {
  // First check for detailed_filters
  if (
    filter &&
    filter.detailed_filters &&
    Array.isArray(filter.detailed_filters) &&
    filter.detailed_filters.length > 0
  ) {
    // Group filters by type
    const filtersByType: Record<string, string[]> = {};

    const siteOfCareLabels = collectSiteOfCareLabels(
      filter.detailed_filters,
      socOptions
    );
    if (siteOfCareLabels.length > 0) {
      filtersByType[SITE_OF_CARE_DISPLAY_TYPE] = siteOfCareLabels;
    }

    // Process each detailed filter
    filter.detailed_filters.forEach((filterItem: any) => {
      const type = filterItem.type;
      if (isSocFilterType(type) || isSocSuppressedFilterType(type)) return;
      const displayType = getDisplayTypeForFilterType(type);

      // Skip if there are no detailed values
      if (
        !filterItem.detailed_values ||
        !Array.isArray(filterItem.detailed_values) ||
        filterItem.detailed_values.length === 0
      ) {
        return;
      }

      // Initialize the array for this type if it doesn't exist
      if (!filtersByType[displayType]) {
        filtersByType[displayType] = [];
      }

      // Add all labels from detailed_values to the array for this type
      filterItem.detailed_values.forEach((detailedValue: any) => {
        if (
          detailedValue?.label &&
          !filtersByType[displayType].includes(detailedValue?.label)
        ) {
          filtersByType[displayType].push(detailedValue?.label);
        }
      });
    });

    // If we found detailed filters, return them
    if (Object.keys(filtersByType).length > 0) {
      return renderFilterElements(filtersByType, filter);
    }
  }

  // Fallback to regular filters if detailed_filters is not available
  if (
    filter &&
    filter.filters &&
    Array.isArray(filter.filters) &&
    filter.filters.length > 0
  ) {
    // Group filters by type
    const filtersByType: Record<string, string[]> = {};

    const siteOfCareLabels = collectSiteOfCareLabels(
      filter.filters,
      socOptions
    );
    if (siteOfCareLabels.length > 0) {
      filtersByType[SITE_OF_CARE_DISPLAY_TYPE] = siteOfCareLabels;
    }

    // Process each filter
    filter.filters.forEach((filterItem: any) => {
      const type = filterItem.type;
      if (isSocFilterType(type) || isSocSuppressedFilterType(type)) return;
      const displayType = getDisplayTypeForFilterType(type);

      // Skip if there are no values
      if (
        !filterItem.values ||
        !Array.isArray(filterItem.values) ||
        filterItem.values.length === 0
      ) {
        return;
      }

      // Initialize the array for this type if it doesn't exist
      if (!filtersByType[displayType]) {
        filtersByType[displayType] = [];
      }

      // Add all values to the array for this type
      filterItem.values.forEach((value: any) => {
        const valueLabel =
          typeof value === 'string' ? value : value?.label || value?.toString();
        if (!filtersByType[displayType]?.includes(valueLabel)) {
          filtersByType[displayType].push(valueLabel);
        }
      });
    });

    // If we found filters, return them
    if (Object.keys(filtersByType).length > 0) {
      return renderFilterElements(filtersByType, filter);
    }
  }

  // If no filter types were found, check if we can extract info from the label
  const filtersByType: Record<string, string[]> = {};
  if (filter?.label?.includes('Oncologist')) {
    filtersByType['Specialty'] = ['Oncology'];
  }
  if (filter?.label?.includes('Johns Hopkins')) {
    filtersByType['Med. School'] = ['Johns Hopkins'];
  }

  // Add company-wide info if applicable
  if (filter && filter?.scope?.toLowerCase() === 'company') {
    filtersByType['Company'] = ['Company-wide filter'];
  }

  return renderFilterElements(filtersByType, filter);
};

// Helper function to render filter elements from grouped filters
const renderFilterElements = (
  filtersByType: Record<string, string[]>,
  _filter: ParentFilterGroup
): ReactNode => {
  // Convert the grouped filters to JSX
  const filterElements: React.JSX.Element[] = [];

  // Add "Includes:" at the beginning
  filterElements.push(
    <span
      key="includes-prefix"
      className="text-tertiary text-xs font-normal font-['Inter'] leading-none"
    >
      Includes:{' '}
    </span>
  );

  Object.entries(filtersByType).forEach(([type, values], index) => {
    // Add separator if not the first item
    if (index > 0) {
      filterElements.push(
        <span
          key={`separator-${index}`}
          className="text-tertiary text-xs font-normal font-['Inter'] leading-none"
        >
          ;{' '}
        </span>
      );
    }

    // Add type label
    filterElements.push(
      <span
        key={`type-${index}`}
        className="text-tertiary text-xs font-normal font-['Inter'] leading-none"
      >
        {type}:{' '}
      </span>
    );

    // Add values
    values.forEach((value, valueIndex) => {
      // Add comma if not the first value
      if (valueIndex > 0) {
        filterElements.push(
          <span
            key={`comma-${index}-${valueIndex}`}
            className="text-tertiary text-xs font-normal font-['Inter'] leading-none"
          >
            ,{' '}
          </span>
        );
      }

      // Add value
      filterElements.push(
        <span
          key={`value-${index}-${valueIndex}`}
          className="text-tertiary text-xs font-bold font-['Inter'] leading-none"
        >
          {value}
        </span>
      );
    });
  });

  // If no filter elements were created (beyond the "Includes:" prefix), return a default message
  if (filterElements.length <= 1) {
    return (
      <span className="text-tertiary text-xs font-normal font-['Inter'] leading-none">
        No filter details available
      </span>
    );
  }

  return filterElements;
};

export default TableFiltersButton;
